import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  alpha,
  Divider,
  Chip,
  CircularProgress,
  Button,
  ButtonGroup,
  Paper,
  Tooltip,
  Stack,
  Badge,
  useTheme
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  Speed as SpeedIcon,
  Person as PersonIcon,
  Bolt as BoltIcon,
  AccessTime as TimeIcon,
  Category as CategoryIcon,
  Star as StarIcon,
  WorkOutline as WorkIcon,
  Favorite as HeartIcon
} from '@mui/icons-material';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, Cell } from 'recharts';
import relatoriosApi from '../../services/relatoriosApi';

const RelatorioAtendentes = ({ dados, customTheme, carregando, filtros, hasLimitedAccess = false }) => {
  const theme = useTheme();
  const [dadosAtendentes, setDadosAtendentes] = useState(null);
  const [carregandoDados, setCarregandoDados] = useState(false);
  // Para usuários com acesso limitado, forçar critério de eficiência (tempo médio)
  const [criterioRanking, setCriterioRanking] = useState(hasLimitedAccess ? 'tempoMedio' : 'tempoMedio');
  const [erro, setErro] = useState(null);

  // Cores para os badges de ranking
  const coresRanking = ['#FFD700', '#C0C0C0', '#CD7F32', customTheme.palette.primary.main, customTheme.palette.grey[500]];

  // Buscar dados dos atendentes
  useEffect(() => {
    if (filtros) {
      buscarDadosAtendentes();
    }
  }, [filtros]);

  // Forçar critério de eficiência para usuários com acesso limitado
  useEffect(() => {
    if (hasLimitedAccess) {
      setCriterioRanking('tempoMedio');
    }
  }, [hasLimitedAccess]);

  const buscarDadosAtendentes = async () => {
    try {
      setCarregandoDados(true);
      setErro(null);

      console.log('[DEBUG] RelatorioAtendentes - Buscando dados com filtros:', filtros);

      const filtrosApi = {
        dataInicio: filtros.dataInicio?.format('YYYY-MM-DD'),
        dataFim: filtros.dataFim?.format('YYYY-MM-DD')
      };

      const response = await relatoriosApi.buscarDadosAtendentes(filtrosApi);
      
      if (response.success) {
        setDadosAtendentes(response.data);
        console.log('[DEBUG] RelatorioAtendentes - Dados carregados:', response.data);
      } else {
        console.error('[DEBUG] RelatorioAtendentes - Erro na resposta da API');
        setErro('Erro ao carregar dados de atendentes');
      }
    } catch (error) {
      console.error('[DEBUG] RelatorioAtendentes - Erro ao buscar dados:', error);
      setErro('Erro ao carregar dados do servidor');
    } finally {
      setCarregandoDados(false);
    }
  };

  // Ordenar atendentes de acordo com o critério selecionado
  const atendentesOrdenados = React.useMemo(() => {
    if (!dadosAtendentes?.atendentes) return [];
    
    return [...dadosAtendentes.atendentes].sort((a, b) => {
      if (criterioRanking === 'tempoMedio') {
        // Extrair os valores numéricos em minutos dos tempos médios
        const tempoA = a.tempoMedioMs || 0;
        const tempoB = b.tempoMedioMs || 0;
        // Ordenar do menor para o maior tempo (mais eficiente vem primeiro)
        return tempoA - tempoB;
      } else {
        return b.totalAtendimentos - a.totalAtendimentos;
      }
    });
  }, [dadosAtendentes, criterioRanking]);

  // Preparar dados para o gráfico de barras
  const dadosGrafico = React.useMemo(() => {
    if (!atendentesOrdenados.length) return [];
    
    return atendentesOrdenados.slice(0, 5).map((atendente, index) => {
      // Para tempo médio, usar o valor já formatado em minutos para o gráfico
      let valorGrafico = criterioRanking === 'tempoMedio' 
        ? atendente.tempoMedioMs 
        : atendente.totalAtendimentos;
        
      // Se for tempo médio e muito grande, converter para minutos para visualização no gráfico
      if (criterioRanking === 'tempoMedio' && valorGrafico > 1000) {
        valorGrafico = Math.round(valorGrafico / (60 * 1000));
      }
      
      return {
        nome: atendente.nome.split(' ')[0], // Apenas o primeiro nome para o gráfico
        nomeCompleto: atendente.nome,
        valor: valorGrafico,
        valorOriginal: criterioRanking === 'tempoMedio' ? atendente.tempoMedioMs : atendente.totalAtendimentos,
        formattedValue: criterioRanking === 'tempoMedio' 
          ? atendente.tempoMedio 
          : atendente.totalAtendimentos,
        fill: coresRanking[index]
      };
    });
  }, [atendentesOrdenados, criterioRanking]);

  // Função para obter o título do badge de acordo com a posição no ranking
  const obterTituloBadge = (index) => {
    switch (index) {
      case 0: return 'Ouro';
      case 1: return 'Prata';
      case 2: return 'Bronze';
      default: return `Top ${index + 1}`;
    }
  };

  // Tooltip customizado para o gráfico
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <Paper
          elevation={3}
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: 'background.paper',
            border: `1px solid ${alpha(customTheme.palette.primary.main, 0.2)}`
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {payload[0].payload.nomeCompleto}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {criterioRanking === 'tempoMedio' ? 'Tempo médio: ' : 'Atendimentos: '}
            <strong>{payload[0].payload.formattedValue}</strong>
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  // Renderizar a visualização de carregamento
  if (carregando || carregandoDados) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Renderizar mensagem de erro
  if (erro) {
    return (
      <Box sx={{ p: 2, bgcolor: alpha('#f44336', 0.1), borderRadius: 2, color: '#f44336' }}>
        <Typography variant="body1">{erro}</Typography>
      </Box>
    );
  }

  // Verificar se temos dados para exibir
  if (!dadosAtendentes || !dadosAtendentes.atendentes || dadosAtendentes.atendentes.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          Nenhum dado de atendentes disponível para o período selecionado
        </Typography>
      </Box>
    );
  }

  return (
    <Card sx={{ borderRadius: 3, mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
            <WorkIcon sx={{ mr: 1, color: customTheme.palette.primary.main }} />
            Desempenho dos Atendentes
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Lista de Atendentes */}
          <Grid item xs={12} md={7}>
            <Box sx={{ 
              p: 2, 
              bgcolor: alpha(customTheme.palette.background.paper, 0.5), 
              borderRadius: 2,
              border: `1px solid ${alpha(customTheme.palette.primary.main, 0.1)}`,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: `0 2px 8px ${alpha(customTheme.palette.primary.main, 0.05)}`
            }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PersonIcon sx={{ mr: 1, color: customTheme.palette.primary.main }} fontSize="small" />
                  Informações dos Atendentes
                </Box>
                {atendentesOrdenados.length > 5 && (
                  <Chip 
                    size="small" 
                    label={`${atendentesOrdenados.length} atendentes`} 
                    sx={{ 
                      bgcolor: alpha(customTheme.palette.primary.main, 0.1),
                      color: customTheme.palette.primary.main,
                      fontWeight: 500,
                      fontSize: '0.7rem'
                    }} 
                  />
                )}
              </Typography>
              
              <List sx={{ 
                width: '100%', 
                overflow: 'auto', 
                pr: 1, 
                flexGrow: 1,
                // Altura para exibir 5 atendentes (aproximadamente 85px por atendente)
                maxHeight: '570px',
                // Estilizar a barra de rolagem
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: alpha(customTheme.palette.primary.main, 0.05),
                  borderRadius: '10px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: alpha(customTheme.palette.primary.main, 0.3),
                  borderRadius: '10px',
                  '&:hover': {
                    background: alpha(customTheme.palette.primary.main, 0.5),
                  }
                }
              }}>
                {atendentesOrdenados.map((atendente, index) => (
                  <React.Fragment key={atendente.id}>
                    <ListItem 
                      alignItems="flex-start"
                      sx={{ 
                        borderRadius: 2,
                        mb: 1,
                        transition: 'all 0.2s',
                        '&:hover': {
                          bgcolor: alpha(customTheme.palette.primary.main, 0.05)
                        }
                      }}
                    >
                      <ListItemAvatar>
                        <Badge
                          overlap="circular"
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          badgeContent={
                            index < 3 ? (
                              <Tooltip title={obterTituloBadge(index)}>
                                <Avatar
                                  sx={{
                                    width: 22,
                                    height: 22,
                                    bgcolor: coresRanking[index],
                                    fontSize: 12,
                                    fontWeight: 'bold'
                                  }}
                                >
                                  {index + 1}
                                </Avatar>
                              </Tooltip>
                            ) : null
                          }
                        >
                          <Avatar 
                            sx={{ 
                              bgcolor: index < 3 
                                ? alpha(coresRanking[index], 0.2) 
                                : alpha(customTheme.palette.primary.main, 0.1),
                              color: index < 3 
                                ? coresRanking[index] 
                                : customTheme.palette.primary.main
                            }}
                          >
                            {atendente.nome.charAt(0)}
                          </Avatar>
                        </Badge>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography 
                            variant="subtitle2" 
                            sx={{ 
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center'
                            }}
                          >
                            {atendente.nome}
                            {index < 3 && (
                              <Tooltip title={`${obterTituloBadge(index)} no ranking de ${criterioRanking === 'tempoMedio' ? 'eficiência' : 'atendimentos'}`}>
                                <TrophyIcon 
                                  fontSize="small" 
                                  sx={{ 
                                    ml: 1, 
                                    color: coresRanking[index],
                                    transform: 'scale(0.8)'
                                  }} 
                                />
                              </Tooltip>
                            )}
                          </Typography>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Grid container spacing={1}>
                              {/* Mostrar número de atendimentos apenas para admin */}
                              {!hasLimitedAccess && (
                                <Grid item xs={6}>
                                  <Typography 
                                    variant="body2" 
                                    color="text.secondary"
                                    sx={{ display: 'flex', alignItems: 'center' }}
                                  >
                                    <BoltIcon fontSize="small" sx={{ mr: 0.5, color: '#ff9800', transform: 'scale(0.8)' }} />
                                    {atendente.totalAtendimentos} atendimentos
                                  </Typography>
                                </Grid>
                              )}
                              <Grid item xs={hasLimitedAccess ? 12 : 6}>
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary"
                                  sx={{ display: 'flex', alignItems: 'center' }}
                                >
                                  <TimeIcon fontSize="small" sx={{ mr: 0.5, color: '#4caf50', transform: 'scale(0.8)' }} />
                                  Tempo médio: {atendente.tempoMedio}
                                </Typography>
                              </Grid>
                              <Grid item xs={hasLimitedAccess ? 12 : 6}>
                                <Typography 
                                  variant="body2" 
                                  color="text.secondary"
                                  sx={{ display: 'flex', alignItems: 'center' }}
                                >
                                  <CategoryIcon fontSize="small" sx={{ mr: 0.5, color: '#2196f3', transform: 'scale(0.8)' }} />
                                  {atendente.servicoMaisAtendido || 'N/A'}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < atendentesOrdenados.length - 1 && (
                      <Divider variant="inset" component="li" />
                    )}
                  </React.Fragment>
                ))}
              </List>
            </Box>
          </Grid>

          {/* Ranking e Gamificação */}
          <Grid item xs={12} md={5}>
            <Box sx={{ 
              p: 2, 
              bgcolor: alpha(customTheme.palette.background.paper, 0.5), 
              borderRadius: 2,
              border: `1px solid ${alpha(customTheme.palette.primary.main, 0.1)}`,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: `0 2px 8px ${alpha(customTheme.palette.primary.main, 0.05)}`
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                  <TrophyIcon sx={{ mr: 1, color: '#FFD700' }} fontSize="small" />
                  Ranking de {hasLimitedAccess ? 'Eficiência' : 'Atendentes'}
                </Typography>
                {/* Mostrar botões de seleção apenas para admin */}
                {!hasLimitedAccess && (
                  <ButtonGroup size="small" variant="outlined">
                    <Button 
                      onClick={() => setCriterioRanking('totalAtendimentos')}
                      variant={criterioRanking === 'totalAtendimentos' ? 'contained' : 'outlined'}
                      sx={{ 
                        fontSize: '0.75rem',
                        bgcolor: criterioRanking === 'totalAtendimentos' 
                          ? customTheme.palette.primary.main 
                          : 'transparent'
                      }}
                    >
                      Atendimentos
                    </Button>
                    <Button 
                      onClick={() => setCriterioRanking('tempoMedio')}
                      variant={criterioRanking === 'tempoMedio' ? 'contained' : 'outlined'}
                      sx={{ 
                        fontSize: '0.75rem',
                        bgcolor: criterioRanking === 'tempoMedio' 
                          ? customTheme.palette.primary.main 
                          : 'transparent'
                      }}
                    >
                      Eficiência
                    </Button>
                  </ButtonGroup>
                )}
              </Box>

              {/* Box de informação */}
              <Box 
                sx={{ 
                  p: 1.5, 
                  mb: 2, 
                  borderRadius: 2, 
                  bgcolor: alpha('#2196f3', 0.1),
                  border: `1px dashed ${alpha('#2196f3', 0.3)}`,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <InfoIcon 
                  sx={{ 
                    color: '#2196f3', 
                    mr: 1.5,
                    transform: 'scale(1.2)'
                  }} 
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  {hasLimitedAccess 
                    ? 'Eficiência baseada no tempo médio de atendimento. Quanto menor, melhor!'
                    : (criterioRanking === 'tempoMedio' 
                      ? 'Eficiência: tempo médio do atendente. Quanto menor, melhor!'
                      : 'Ranking baseado no total de atendimentos realizados no período.')
                  }
                </Typography>
              </Box>

              {/* Conteúdo principal com flexGrow */}
              <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                {/* Exibição dos top 3 como cards especiais */}
                <Stack 
                  direction="row" 
                  spacing={2} 
                  justifyContent="center" 
                  sx={{ mb: 3 }}
                >
                  {dadosGrafico.slice(0, 3).map((item, index) => (
                    <Card 
                      key={index}
                      sx={{ 
                        width: '30%',
                        p: 1,
                        textAlign: 'center',
                        borderRadius: 2,
                        bgcolor: alpha(coresRanking[index], 0.1),
                        border: `2px solid ${coresRanking[index]}`,
                        boxShadow: `0 4px 12px ${alpha(coresRanking[index], 0.2)}`,
                        transform: index === 0 ? 'scale(1.05)' : 'scale(1)'
                      }}
                    >
                      <Box sx={{ mb: 1 }}>
                        <Tooltip title={obterTituloBadge(index)}>
                          <TrophyIcon sx={{ color: coresRanking[index], fontSize: '2rem' }} />
                        </Tooltip>
                      </Box>
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          mb: 0.5
                        }}
                      >
                        {item.nome}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.formattedValue}
                      </Typography>
                    </Card>
                  ))}
                </Stack>

                {/* Gráfico de barras para o ranking */}
                <Box sx={{ height: 250, width: '100%' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      layout="vertical"
                      data={dadosGrafico}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <XAxis type="number" hide />
                      <YAxis 
                        dataKey="nome" 
                        type="category" 
                        axisLine={false}
                        tickLine={false}
                        width={80}
                        tick={{ fontSize: 12, fontWeight: 500 }}
                      />
                      <RechartsTooltip 
                        content={<CustomTooltip />}
                        cursor={{ fill: alpha(customTheme.palette.primary.main, 0.1) }}
                      />
                      <Bar 
                        dataKey="valor" 
                        barSize={20}
                        radius={4}
                        label={{ 
                          position: 'right', 
                          formatter: (value, entry) => {
                            if (entry && entry.payload) {
                              // Usar o valor formatado (ex: "9 min") em vez do valor bruto
                              return entry.payload.formattedValue;
                            }
                            // Se for tempo médio e não tiver formatação, adicionar "min"
                            return criterioRanking === 'tempoMedio' ? `${value} min` : value;
                          },
                          fill: theme.palette.text.primary,
                          fontSize: 12,
                          fontWeight: 500
                        }}
                      >
                        {dadosGrafico.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>

                {/* Mensagem motivacional */}
                <Box 
                  sx={{ 
                    mt: 2,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: alpha('#4caf50', 0.1),
                    border: `1px solid ${alpha('#4caf50', 0.2)}`,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <HeartIcon sx={{ color: '#e91e63', mr: 1.5 }} />
                  <Typography variant="body2" color="text.secondary">
                    <strong>Parabéns {dadosGrafico[0]?.nomeCompleto || 'ao destaque'}!</strong> Mantendo o padrão de excelência em nossos atendimentos.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

// Componente para o ícone de informação
const InfoIcon = (props) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" fill="currentColor" fillOpacity="0.2" />
    <path d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default RelatorioAtendentes; 